import userPrepaidCardQueries from '@apis/queries/userPrepaidCardQueries';
import BlankEtc from '@components/blank/BlankEtc';
import Button from '@components/Button';
import Column from '@components/Column';
import CtaLayout from '@components/CtaLayout';
import Description from '@components/Description';
import Divider from '@components/Divider';
import Header from '@components/Header';
import Image from '@components/Image';
import Loading from '@components/Loading';
import RadioCircle from '@components/Radio/RadioCircle';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import InfoIcon from '@icons/InfoIcon';
import { TransferablePrepaidCard } from '@models/prepaidCard';
import { AppScreen } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType, useFlow } from '@stackflow/react';
import { pressActionEffect } from '@styles/mixin';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface Params {
    prepaidCardId: string;
}

declare module '@stackflow/config' {
    interface Register {
        PrepaidCardTransferActivity: Params;
    }
}

const PrepaidCardTransferActivity: ActivityComponentType<'PrepaidCardTransferActivity'> = ({ params }) => {
    const { prepaidCardId } = params;
    const { t } = useTranslation('prepaidCard');
    const { push } = useFlow();
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const { data: source, status: sourceStatus } = useQuery(userPrepaidCardQueries.detail(prepaidCardId));
    const { data: transferable, status: listStatus } = useQuery(userPrepaidCardQueries.transferable(prepaidCardId));

    const targets = transferable?.cards ?? [];
    const selected = targets.find((card) => card.id === selectedId);
    const isLoading = sourceStatus === 'pending' || listStatus === 'pending';
    const isEmpty = !isLoading && targets.length === 0;

    const handleSubmit = () => {
        if (!source || !selected) return;
        if (source.balance <= 0) {
            toast.error(t('transfer.zeroBalance'));
            return;
        }
        if (source.balance > selected.remainingChargeLimit) {
            toast.error(t('transfer.limitExceeded'));
            return;
        }
        push('TransferConfirmModal', { prepaidCardId, targetCardId: selected.id });
    };

    return (
        <AppScreen>
            <CtaLayout
                header={<Header title={t('transfer.title')} />}
                footer={(
                    <Button fullWidth
                        disabled={!selected}
                        onClick={handleSubmit}>
                        {t('transfer.submit')}
                    </Button>
                )}>
                {isLoading && <Loading size='small' />}
                {source && (
                    <>
                        <SourceSection>
                            <SourceCard fullWidth spacing={4} align='center'>
                                <Image src={source.imageUrl}
                                    width={90}
                                    height={60}
                                    alt={source.name}
                                    css={css`
                                        border-radius: 8px;
                                    `}
                                />
                                <Column spacing={1} align='flex-start'>
                                    <Text size='title' fontWeight='medium' align='left' multiline>
                                        {source.name}
                                    </Text>
                                    <Text size='body-short' color={colors.textSecondary} align='left' multiline>
                                        {t('transfer.sourceBalance', {
                                            amount: source.balance.toLocaleString(),
                                        })}
                                    </Text>
                                </Column>
                            </SourceCard>
                        </SourceSection>
                        <Divider />
                    </>
                )}
                {!isLoading && (
                    <ListSection fullWidth spacing={3}>
                        {!isEmpty && (
                            <Description>
                                <Row fullWidth spacing={1.5} align='center'>
                                    <InfoIcon size={12} variant='fill' />
                                    <Text size='body-long' fontWeight='regular' color={colors.textSecondary}>
                                        {t('transfer.selectHint')}
                                    </Text>
                                </Row>
                            </Description>
                        )}
                        {isEmpty ? (
                            <BlankEtc>{t('transfer.empty')}</BlankEtc>
                        ) : (
                            <Column fullWidth spacing={2}>
                                {targets.map((card) => (
                                    <TargetItem
                                        key={card.id}
                                        card={card}
                                        isSelected={selectedId === card.id}
                                        onSelect={setSelectedId}
                                    />
                                ))}
                            </Column>
                        )}
                    </ListSection>
                )}
            </CtaLayout>
        </AppScreen>
    );
};

interface TargetItemProps {
    card: TransferablePrepaidCard;
    isSelected: boolean;
    onSelect: (id: string) => void;
}

function TargetItem({ card, isSelected, onSelect }: TargetItemProps) {
    const { t } = useTranslation('prepaidCard');
    const disabled = !card.transferable;

    return (
        <TargetButton type='button'
            disabled={disabled}
            onClick={() => onSelect(card.id)}
            css={pressActionEffect}
            data-selected={isSelected}>
            <Row spacing={4} align='center' fullWidth>
                <CardThumb src={card.imageUrl}
                    width={90}
                    height={60}
                    alt={card.name} />
                <TargetInfo>
                    <Text size='title' fontWeight='medium' ellipsis>
                        {card.name}
                    </Text>
                    <Text size='title' fontWeight='bold' color={colors.textSecondary}>
                        {card.balance.toLocaleString()}{t('currencyUnit')}
                    </Text>
                </TargetInfo>
                <RadioCircle isSelected={isSelected} />
            </Row>
        </TargetButton>
    );
}

const SourceSection = styled.div`
    background-color: var(--color-gray2);
    width: 100%;
`;

const SourceCard = styled(Row)`
    background-color: var(--color-white);
    padding: 16px;
`;

const ListSection = styled(Column)`
    padding: 16px 20px 24px;
`;

const TargetButton = styled.button`
    background: var(--color-white);
    padding: 16px 0;
    width: 100%;

    &[data-selected='true'] {
        border-color: var(--color-primary);
    }

    &:disabled {
        opacity: 0.5;
    }
`;

const CardThumb = styled(Image)`
    border-radius: 4px;
    flex-shrink: 0;
    object-fit: cover;
`;

const TargetInfo = styled.div`
    flex: 1;
    min-width: 0;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export default PrepaidCardTransferActivity;
